import React, { useEffect, useState } from "react";
import { API_BASE } from "./config/api";
import SessionActivityMonitor from "./components/SessionActivityMonitor";
import RtaPasswordsModal from "./RtaPasswordsModal";
import { Layout, Menu, Button, Drawer, Grid, Tooltip, message } from "antd";
import { useNavigate, Outlet, Navigate } from "react-router-dom";
import { clearDistributorSession, getDistributorToken } from "./config/auth";
import {
  disableBiometricLogin,
  enableBiometricLogin,
  getBiometricCapability,
  hasBiometricLogin,
  isBiometricCancellation,
  isNativeBiometricRealm,
  readBiometricLogin,
} from "./config/biometricAuth";
import {
  LockOutlined,
  KeyOutlined,
  TeamOutlined,
  TableOutlined,
  PieChartOutlined,
  SlidersOutlined,
  UserAddOutlined,
  LogoutOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  FilePdfOutlined, // ✅ ADDED
  FileTextOutlined,
  SafetyOutlined,
} from "@ant-design/icons";

/*import {
  LockOutlined,
  KeyOutlined,
  TeamOutlined,
  TableOutlined,
  PieChartOutlined,
  SlidersOutlined,
  UserAddOutlined,
  LogoutOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
*/
const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

// Helper: Title Case
const toTitleCase = (str = "") => {
  return String(str)
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getIsAdminFromStorage = () => {
  const raw =
    localStorage.getItem("is_admin") ??
    localStorage.getItem("isadmin") ??
    localStorage.getItem("admin") ??
    localStorage.getItem("isAdmin");
  if (raw == null) return false;
  const val = String(raw).trim().toLowerCase();
  return val === "true" || val === "1" || val === "yes" || val === "y";
};

function AppLayout() {
  const authToken = getDistributorToken();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isDesktop = screens.lg;

  const arnHolderName = toTitleCase(localStorage.getItem("arn_holder_name") || "");
  const isAdmin = getIsAdminFromStorage();

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rtaOpen, setRtaOpen] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isNativeBiometricRealm("mfd")) return;
      const capability = await getBiometricCapability("mfd");
      const enabled = capability.available ? await hasBiometricLogin("mfd") : false;
      if (alive) {
        setBiometricAvailable(capability.available);
        setBiometricEnabled(enabled);
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggleBiometric = async () => {
    if (biometricBusy || !biometricAvailable) return;
    setBiometricBusy(true);
    try {
      if (biometricEnabled) {
        // Reading the stored device credential requires biometric authentication.
        // Revoke it on the server first; only then remove the local secure copy.
        const saved = await readBiometricLogin("mfd");
        if (!saved?.deviceCredential) {
          await disableBiometricLogin("mfd");
          setBiometricEnabled(false);
          message.success("Biometric login disabled on this device.");
          return;
        }

        await fetch(`${API_BASE}/distributor_biometric_revoke.php`, {
          method: "POST",
          headers: {
            "X-KG-Biometric-Authorization": `Bearer ${saved.deviceCredential}`,
          },
        }).then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data?.status !== "ok") {
            throw new Error(data?.message || "Unable to revoke biometric credential.");
          }
        });

        await disableBiometricLogin("mfd");
        setBiometricEnabled(false);
        message.success("Biometric login disabled on this device.");
      } else {
        const token = getDistributorToken();
        if (!token) throw new Error("Session expired.");

        const enrollRes = await fetch(`${API_BASE}/distributor_biometric_enroll.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-KG-Distributor-Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ device_label: "MFSetu - MFD" }),
        });
        const enrollData = await enrollRes.json().catch(() => ({}));
        const deviceCredential = enrollData?.device_credential;
        if (!enrollRes.ok || enrollData?.status !== "ok" || !deviceCredential) {
          throw new Error(enrollData?.message || "Biometric enrollment failed.");
        }

        await enableBiometricLogin("mfd", {
          deviceCredential,
          userId: localStorage.getItem("arn_number") || "",
          displayName: localStorage.getItem("arn_holder_name") || "",
          isAdmin,
        });
        setBiometricEnabled(true);
        message.success("Biometric login enabled on this device.");
      }
    } catch (error) {
      if (!isBiometricCancellation(error)) message.error("Unable to change biometric login setting.");
    } finally {
      setBiometricBusy(false);
    }
  };

  // Do not render protected distributor content without a session token.
  if (!authToken) return <Navigate to="/login" replace />;

  // ---- MENU ITEMS ----
  /*const baseItems = [
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      onClick: () => navigate("/dashboard"),
    },
    {
      key: "ledger",
      icon: <TableOutlined />,
      label: "View Ledger",
      children: [
        {
          key: "ledger-individual",
          label: "View Individual Ledgers",
          onClick: () => navigate("/ledger"),
        },
        {
          key: "ledger-family",
          label: "View Family Group Ledgers",
          onClick: () => navigate("/family_ledger"),
        },
      ],
    },
    {
      key: "family-group",
      icon: <TeamOutlined />,
      label: "Manage Family Group",
      onClick: () => navigate("/managefamilygroup"),
    },
    {
      key: "change-arn-password",
      icon: <LockOutlined />,
      label: "Change Login Password",
      onClick: () => navigate("/change-arn-password"),
    },
  ];
*/
const baseItems = [
  {
    key: "dashboard",
    icon: <DashboardOutlined />,
    label: "Dashboard",
    onClick: () => navigate("/dashboard"),
  },
  {
    key: "ledger",
    icon: <TableOutlined />,
    label: "View Ledger",
    children: [
      {
        key: "ledger-individual",
        label: "View Individual Ledgers",
        onClick: () => navigate("/ledger"),
      },
      {
        key: "ledger-family",
        label: "View Family Group Ledgers",
        onClick: () => navigate("/family_ledger"),
      },
     {
      key: "ledger-amc-scheme",
      label: "View AMC & Scheme Wise Reports",
      onClick: () => navigate("/amc-scheme-report"),
    },
    ],
  },
  {
    key: "reports",
    icon: <FileTextOutlined />,
    label: "Reports",
    children: [
      {
        key: "client-master-report",
        label: "Generate Client Master Report",
        onClick: () => navigate("/client-master-report"),
      },
      {
        key: "report-downloads",
        label: "Downloads",
        onClick: () => navigate("/report-downloads"),
      },
    ],
  },
  {
    key: "family-group",
    icon: <TeamOutlined />,
    label: "Manage Family Group",
    onClick: () => navigate("/managefamilygroup"),
  },
  {
    key: "change-arn-password",
    icon: <LockOutlined />,
    label: "Change Login Password",
    onClick: () => navigate("/change-arn-password"),
  },
  ...(biometricAvailable ? [{
    key: "biometric-login",
    icon: <SafetyOutlined />,
    label: biometricBusy
      ? "Biometric Login..."
      : `Biometric Login: ${biometricEnabled ? "ON" : "OFF"}`,
    onClick: toggleBiometric,
  }] : []),

  // ✅ NEW MENU OPTION (ONLY THIS IS ADDED)
  {
    key: "upload-health-policy",
    icon: <FilePdfOutlined />,
    label: "Upload Health Insurance Pdf",
    onClick: () => navigate("/upload-health-policy"),
  },
];

  if (isAdmin) {
    baseItems.push(
      {
        key: "scheme-type-mapping",
        icon: <SlidersOutlined />,
        label: "Scheme Type Mapping",
        onClick: () => navigate("/schemetypemapping"),
      },
      {
        key: "distributor-onboarding",
        icon: <UserAddOutlined />,
        label: "Distributor On Boarding",
        onClick: () => navigate("/arn_creation"),
      },
      {
        key: "show-rta-passwords",
        icon: <KeyOutlined />,
        label: "Show RTA File Passwords",
        onClick: () => setRtaOpen(true),
      }
    );
  }

  const handleMenuClick = (item) => {
    item.onClick?.();
    if (!isDesktop) setDrawerOpen(false);
  };

  // ---- Sidebar Header ----
  const SidebarHeader = (
    <div
      style={{
        padding: "10px 10px",
        borderBottom: "1px solid #f0f0f0",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {isDesktop && (
        <Tooltip title={collapsed ? "Expand" : "Collapse"}>
          <Button
            type="text"
            size="small"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((c) => !c)}
          />
        </Tooltip>
      )}

      <div
        style={{
          fontWeight: 700,
          fontSize: 15,
          display: "flex",
          alignItems: "center",
          gap: 8,
          flex: 1,
          whiteSpace: "normal",   // ✅ allow wrapping
          overflow: "hidden",
        }}
      >
        <PieChartOutlined /> {arnHolderName || "Distributor"}
      </div>

      <Tooltip title="Logout">
        <Button
          type="text"
          icon={<LogoutOutlined />}
          danger
          onClick={() => {
            clearDistributorSession();
            window.location.replace("/login");
          }}
        />
      </Tooltip>
    </div>
  );

  // ---- Sidebar Menu ----
  const SidebarMenu = (
    <Menu
      mode="inline"
      selectable={false}
      items={baseItems.map((item) => ({
        ...item,
        onClick: () => handleMenuClick(item),
        children: item.children
          ? item.children.map((child) => ({
              ...child,
              onClick: () => handleMenuClick(child),
            }))
          : undefined,
      }))}
    />
  );

  return (
    <>
      <SessionActivityMonitor realm="distributor" />
      <Layout style={{ minHeight: "100vh" }}>
      {!isDesktop && (
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<MenuOutlined />}
          onClick={() => setDrawerOpen(true)}
          style={{
            position: "fixed",
            top: 12,
            left: 12,
            zIndex: 1000,
          }}
          aria-label="Open menu"
        />
      )}

      <Layout>
        {isDesktop && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            width={240}
            theme="dark"
            style={{ background: "#0f3d8a", boxShadow: "4px 0 20px rgba(15,61,138,.12)" }}
          >
            {SidebarHeader}
            {SidebarMenu}
          </Sider>
        )}

        {!isDesktop && (
          <Drawer
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PieChartOutlined /> <span>{arnHolderName || "Distributor"}</span>
              </div>
            }
            placement="left"
            onClose={() => setDrawerOpen(false)}
            open={drawerOpen}
            bodyStyle={{ padding: 0 }}
            extra={
              <Tooltip title="Logout">
                <Button
                  type="text"
                  icon={<LogoutOutlined />}
                  danger
                  onClick={() => {
                    clearDistributorSession();
                    window.location.replace("/login");
                  }}
                />
              </Tooltip>
            }
          >
            {SidebarMenu}
          </Drawer>
        )}

        <Content style={{ background: "#fff", minHeight: "100vh" }}>
          <Outlet />
        </Content>
      </Layout>
      <RtaPasswordsModal open={rtaOpen} onClose={() => setRtaOpen(false)} />
      </Layout>
    </>
  );
}

export default AppLayout;
